<script lang="ts">
  import { tooltip } from "@kontextwerk/web-sdk/core/overlay/tooltip"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import type { PublicAppointmentSlot } from "../../../../../../../../../api/public/appointment-portal"
  import {
    FLEX_SLOT_TOOLTIP,
    customerWindowStart,
    formatDate,
    formatSlotPrimary,
    formatSlotSecondary,
    formatSlotTypeLabel,
    isFlexSlot,
    slotHasDeadlineWarning,
    slotKey,
  } from "../../_logic/appointment-portal"
  let {
    slots,
    selectedSlot,
    dateSelected = true,
    loading = false,
    error = null,
    mobileVisibleCount = 8,
    mobileBatchSize = 8,
    title = "Verfügbare Zeiten",
    showDate = false,
    onselect,
    onretry,
    onshowmore,
  }: {
    slots: PublicAppointmentSlot[]
    selectedSlot: PublicAppointmentSlot | null
    dateSelected?: boolean
    loading?: boolean
    error?: string | null
    mobileVisibleCount?: number
    mobileBatchSize?: number
    title?: string
    showDate?: boolean
    onselect: (slot: PublicAppointmentSlot) => void
    onretry: () => void
    onshowmore: () => void
  } = $props()

  const selectedKey = $derived(selectedSlot ? slotKey(selectedSlot) : null)
  const remainingMobileSlots = $derived(Math.max(0, slots.length - mobileVisibleCount))
  const nextMobileBatchSize = $derived(Math.min(mobileBatchSize, remainingMobileSlots))
  const mobileShowMoreLabel = $derived(
    nextMobileBatchSize === 1
      ? "Eine weitere Zeit anzeigen"
      : `Weitere ${nextMobileBatchSize} Zeiten anzeigen`,
  )
</script>

<section class="time-slot-list" aria-label={title}>
  <div class="time-header">
    <div>
      <p>{title}</p>
    </div>
  </div>

  <div class="slot-content" aria-busy={loading}>
    {#if loading}
      <div class="loading-state" role="status" aria-live="polite">
        <span class="loading-bar" aria-hidden="true"></span>
        <strong>Freie Zeiten werden geladen</strong>
      </div>
    {:else if error}
      <div class="empty-state">
        <p>{error}</p>
        <button type="button" onclick={onretry}>Erneut laden</button>
      </div>
    {:else if !slots.length}
      <p class="message">
        {!dateSelected
          ? "Bitte wählen Sie einen Tag aus."
          : "Für diesen Tag sind aktuell keine Termine verfügbar. Bitte wählen Sie einen anderen Tag."}
      </p>
    {:else}
      <div class="slots" id="appointment-slot-grid">
        {#each slots as slot, index (slotKey(slot))}
          {@const currentSlotKey = slotKey(slot)}
          {@const selected = selectedKey === currentSlotKey}
          {@const flex = isFlexSlot(slot)}
          {@const deadlineWarning = slotHasDeadlineWarning(slot)}
          <div class="slot-card" class:slot-hidden-on-mobile={index >= mobileVisibleCount}>
            <button
              type="button"
              class="slot-option"
              class:selected
              class:flex
              class:deadline-warning={deadlineWarning}
              disabled={loading}
              aria-pressed={selected}
              onclick={() => {
                if (!loading) onselect(slot)
              }}
            >
              {#if selected}
                <span class="slot-corner-badge slot-corner-badge--selected">Ausgewählt</span>
              {/if}
              {#if deadlineWarning}
                <span class="slot-corner-badge slot-corner-badge--deadline">Nach Frist</span>
              {/if}
              {#if showDate}
                <span class="slot-date">{formatDate(customerWindowStart(slot))}</span>
              {/if}
              <span class="slot-main">{formatSlotPrimary(slot)}</span>
              {#if formatSlotSecondary(slot)}
                <small>{formatSlotSecondary(slot)}</small>
              {/if}
              <span
                class="slot-type-badge"
                class:slot-type-badge--flex={flex}
                use:tooltip={flex ? { content: FLEX_SLOT_TOOLTIP } : undefined}
              >
                {formatSlotTypeLabel(slot)}
              </span>
            </button>
          </div>
        {/each}
      </div>
      {#if remainingMobileSlots > 0}
        <div class="mobile-slot-reveal">
          <CTA
            type="tertiaryButton"
            content={mobileShowMoreLabel}
            darkMode={false}
            onClick={onshowmore}
            fullWidth={true}
          />
        </div>
      {/if}
      {#if slots.length > mobileBatchSize}
        <p class="mobile-slot-status" role="status" aria-live="polite">
          {Math.min(mobileVisibleCount, slots.length)} von {slots.length} Zeiten angezeigt
        </p>
      {/if}
    {/if}
  </div>
</section>

<style>
  .time-slot-list {
    display: grid;
    gap: 0.9rem;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    color: var(--portal-ink, var(--bg-100));
    box-sizing: border-box;
  }

  .time-header {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    align-items: flex-start;
  }

  .time-header p,
  .message,
  .empty-state p {
    margin: 0;
  }

  .time-header p {
    font-weight: 800;
    color: var(--portal-ink, var(--bg-100));
  }

  .message,
  .empty-state p {
    color: var(--portal-muted, #42514f);
  }

  .slot-content {
    position: relative;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .slots {
    --slot-min-width: 14rem;

    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, var(--slot-min-width)), 1fr));
    gap: 0.65rem;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .slot-card {
    position: relative;
    min-width: 0;
  }

  .slot-option {
    position: relative;
    width: 100%;
    min-height: 5.4rem;
    min-width: 0;
    display: grid;
    justify-items: start;
    align-content: center;
    gap: 0.25rem;
    text-align: left;
    padding: 1.35rem 0.85rem 1.15rem;
    border: 1px solid var(--portal-line, #d6ddd8);
    background: #fff;
    color: var(--portal-ink, var(--bg-100));
    cursor: pointer;
    font: inherit;
    box-sizing: border-box;
  }

  .slot-option:disabled {
    cursor: wait;
  }

  .slot-option:hover:not(:disabled),
  .slot-option.selected {
    border-color: var(--primary-200);
  }

  .slot-option:hover:not(:disabled) {
    background: color-mix(in srgb, var(--primary-200) 7%, #fff);
  }

  .slot-option.selected {
    background: color-mix(in srgb, var(--primary-200) 10%, #fff);
    box-shadow: inset 0 0 0 1px var(--primary-200);
  }

  .slot-option:focus-visible {
    outline: 3px solid color-mix(in srgb, var(--primary-200) 70%, #fff);
    outline-offset: 2px;
  }

  .slot-option.flex {
    border-style: dashed;
  }

  .slot-option.deadline-warning {
    border-color: #dc2626;
    background: #fff7f7;
  }

  .slot-option.deadline-warning:hover:not(:disabled),
  .slot-option.deadline-warning.selected {
    border-color: #b91c1c;
    background: #fee2e2;
  }

  .slot-main {
    max-width: 100%;
    font-weight: 900;
    overflow-wrap: anywhere;
  }

  .slot-date {
    max-width: 100%;
    color: var(--portal-muted, #42514f);
    font-size: 0.78rem;
    font-weight: 800;
    text-transform: uppercase;
  }

  .slot-corner-badge {
    position: absolute;
    top: 0;
    z-index: 1;
    padding: 0.18rem 0.36rem;
    background: var(--primary-200);
    color: var(--text-invers-100, #fff);
    font-size: 0.58rem;
    line-height: 1;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0;
  }

  .slot-corner-badge--selected {
    left: 0;
  }

  .slot-corner-badge--deadline {
    right: 0;
    background: #dc2626;
  }

  .slot-type-badge {
    border: 1px solid var(--text-invers-100);
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 1;
    padding: 0.18rem 0.36rem;
    background: transparent;
    color: var(--text-invers-100);
    font-size: 0.58rem;
    line-height: 1;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0;
  }

  .slot-type-badge--flex {
    cursor: help;
  }

  small {
    max-width: 100%;
    color: var(--portal-muted, #42514f);
    overflow-wrap: anywhere;
  }

  .empty-state {
    display: flex;
    gap: 0.8rem;
    align-items: center;
    flex-wrap: wrap;
  }

  .empty-state button {
    border: 1px solid var(--primary-200);
    background: transparent;
    color: var(--primary-200);
    padding: 0.45rem 0.75rem;
    font: inherit;
    font-weight: 800;
    text-transform: uppercase;
    cursor: pointer;
  }

  .loading-state {
    min-height: 5.4rem;
    display: grid;
    align-content: center;
    gap: 0.7rem;
    padding: 0.9rem 1rem;
    border: 1px solid var(--portal-line, #d6ddd8);
    background: var(--portal-subtle, #f7faf8);
    color: var(--portal-ink, var(--bg-100));
    box-sizing: border-box;
  }

  .loading-state strong {
    font-size: 0.82rem;
    text-transform: uppercase;
  }

  .loading-bar {
    position: relative;
    height: 0.28rem;
    overflow: hidden;
    background: color-mix(in srgb, var(--portal-line, #d6ddd8) 72%, #fff);
  }

  .loading-bar::after {
    content: "";
    position: absolute;
    inset: 0;
    width: 45%;
    background: var(--primary-200);
    animation: loading-slide 1.05s ease-in-out infinite;
  }

  .mobile-slot-reveal {
    display: none;
    margin-top: 0.65rem;
  }

  .mobile-slot-status {
    display: none;
  }

  @keyframes loading-slide {
    0% {
      transform: translateX(-110%);
    }

    100% {
      transform: translateX(240%);
    }
  }

  @media (max-width: 560px) {
    .time-slot-list {
      gap: 0.7rem;
    }

    .time-header {
      gap: 0.45rem;
    }

    .slot-content {
      min-height: 0;
    }

    .slots {
      grid-template-columns: minmax(0, 1fr);
      gap: 0.5rem;
    }

    .slot-option {
      min-height: 4.8rem;
      padding: 1.25rem 0.75rem 1.05rem;
    }

    .slot-card.slot-hidden-on-mobile {
      display: none;
    }

    .mobile-slot-reveal {
      display: block;
    }

    .mobile-slot-status {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
      display: block;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .loading-bar::after {
      animation: none;
      width: 100%;
    }
  }
</style>
